<script setup lang="ts">
import { computed } from 'vue';
import { Table as ATable } from 'ant-design-vue';
import { array, func, object } from 'vue-types';

import useFileContentServicesColumns from './Search/useFileContentServiceColumns';

import type {
  FileContentServiceFragmentFragment,
  FileFragmentFragment,
  FileContentFragmentFragment,
} from '@/graphql/generated/graphql';
import Table, { props as properties } from '@/ui/Table/Table.vue';
import TableSizeSelect from '@/ui/Table/TableSizeSelect.vue';
import TableColumnSelect from '@/ui/Table/TableColumnSelect.vue';
import useConfig from '@/providers/config/useConfig';
import TableColumn from '@/ui/Table/templates/TableColumn.vue';
import Refetch from '@/ui/Table/templates/Refetch.vue';
import ViewIconButton from '@/ui/ViewIconButton.vue';
import CopyIconButton from '@/ui/CopyIconButton.vue';

const props = defineProps({
  ...properties,
  dataSource: array<FileContentServiceFragmentFragment>().isRequired,
  getLink: func<(data: FileContentServiceFragmentFragment) => string>(),
  onClick: func<(data: FileContentServiceFragmentFragment) => void>(),
  onSelect: func<(data: FileContentFragmentFragment) => void>(),
  refetch: func(),
  file: object<FileFragmentFragment>().isRequired,
});
const {
  columnsSelected,
  columnsFilter,
  setSorter,
  columns,
} = useFileContentServicesColumns({
  orderBy: props.query.orderBy,
});
const config = useConfig();
const tableDensity = computed(() => config.config.tableDensity);

</script>
<template>
  <Table
    :set-sorter="setSorter"
    placeholder="Search file content rows"
    top-pag-class="flex flex-wrap items-center justify-between gap-4 mb-4"
    :pagination="pagination"
    :loading="loading"
    :query="query"
    :set-query="setQuery"
    :search="search"
    :set-search="setSearch"
    :hide-search="hideSearch"
    :header-class="headerClass"
  >
    <template #top_pag_left>
      <slot name="top_pag_left" />
    </template>
    <template #top_pag_right>
      <div class="flex space-x-2">
        <TableColumnSelect v-model="columnsSelected" :columns-filter="columnsFilter" />
        <TableSizeSelect />
        <Refetch v-if="refetch" :refetch="refetch" />
      </div>
    </template>
    <template #default="{ handleTableChange }">
      <ATable
        :data-source="dataSource"
        :columns="columns"
        :row-key="(record) => record.id"
        :pagination="false"
        :size="tableDensity"
        :default-expand-all-rows="true"
        @change="handleTableChange"
      >
        <template #bodyCell="{ record, column }">
          <template v-if="column.key === 'error_response'">
            <template v-if="record.error_response">
              <CopyIconButton :value="record.error_response" size="small" />
              <ViewIconButton size="small">
                {{ record.error_response }}
              </ViewIconButton>
            </template>
          </template>
          <TableColumn
            v-else
            :column="(column.key as string)"
            :record="record"
            :get-link="getLink"
            :on-click="onClick"
          />
        </template>
        <template #expandedRowRender="{ record }">
          <FileContentTable
            :data-source="record.contents"
            :table-density="tableDensity"
            :file="file"
            :on-select="onSelect"
          />
        </template>
      </ATable>
    </template>
  </Table>
</template>
